import React, { useState } from 'react';
import { FiLink, FiCreditCard, FiSmartphone } from 'react-icons/fi';
import { platformIcon, CUSTOM_PLATFORM_KEY } from '../utils/socialPlatforms';
import { isPaymentPlatform, inPaymentGroup } from '../utils/socialPlatformRegistry';
import './PlatformIcon.css';

// Icons are addressed by convention -- /images/social-network-<key>.png,
// see socialPlatforms.js -- which means adding a platform's icon is just
// dropping a file in, with no code change. The gap in that convention is
// what happens before the file exists: a bare <img> renders a broken
// image, which is worse than no icon at all and worse still on a public
// profile someone is about to send money from.
//
// So the image is attempted first and a glyph stands in if it 404s. When
// the real artwork lands, nothing here needs to change -- the <img>
// simply stops failing.
const FallbackGlyph = ({ platform, size }) => {
  if (platform === CUSTOM_PLATFORM_KEY) return <FiLink size={size} />;
  // An IBAN is a bank account; the rest are mobile-number wallets.
  if (platform === 'iban') return <FiCreditCard size={size} />;
  if (isPaymentPlatform(platform)) return <FiSmartphone size={size} />;
  return <FiLink size={size} />;
};

// The social icons are all full-bleed coloured rounded squares drawn to
// the same brief. The payment artwork is not: it is whatever each bank
// and provider publishes as a favicon, so it ranges from a full-bleed
// tile (Cash App, Wise) to a wordmark adrift in white space (HBL, 24% of
// its canvas). Sitting unframed in the same column, the sparse ones read
// as unfinished.
//
// The tile gives that column one shape: a rounded container of fixed
// size, with the artwork centred inside it. What it deliberately does
// NOT do is inset the artwork by a fixed amount. That would shrink the
// providers whose logo is already a full-bleed tile, ringing them in
// grey to fix a problem they do not have. Instead the inset is baked
// into each PNG when it is fetched -- a sparse mark is scaled to a
// consistent proportion of its canvas and centred on transparency, while
// a full-bleed one is left alone -- so here every image simply fills its
// tile and the tile's own background shows through whatever margin the
// artwork brought with it. CSS cannot measure ink; the asset step can.
//
// Applied to the payment group only. The socials already look like this
// by construction, and framing them would add a border to artwork that
// is already a tile.
const Tile = ({ children }) => <span className="platform-icon-tile">{children}</span>;

// `src` is an optional more-specific icon to try before the platform's
// own -- an IBAN's bank logo, resolved from the IBAN itself (see
// pakistaniBanks.js). It is a chain rather than a straight override
// because the two files land independently: a bank whose row exists but
// whose logo has not been dropped in yet should fall back to the generic
// IBAN icon, not all the way to a glyph, and it should do that without
// anyone having to remember to remove the row.
//
// Each candidate that 404s advances to the next; running out lands on
// the glyph, which is the same end state this component already had.
export default function PlatformIcon({ platform, size = 40, alt = '', src = null }) {
  const [attempt, setAttempt] = useState(0);
  const tiled = inPaymentGroup(platform);

  // Inside a tile the glyph is a mark on a field, not the whole icon, so
  // it is drawn smaller than the tile rather than filling it edge to
  // edge the way the artwork does.
  const wrap = (node) => (tiled ? <Tile>{node}</Tile> : node);
  const glyph = <FallbackGlyph platform={platform} size={tiled ? Math.round(size * 0.55) : size} />;

  // Custom never had artwork by design, so it goes straight to the glyph
  // rather than requesting a file known not to exist.
  if (platform === CUSTOM_PLATFORM_KEY) return wrap(glyph);

  const candidates = src ? [src, platformIcon(platform)] : [platformIcon(platform)];

  if (attempt >= candidates.length) return wrap(glyph);

  const current = candidates[attempt];

  return wrap(
    <img
      // Keyed by the URL so React swaps the element rather than mutating
      // src on the existing one -- a browser that has already fired error
      // for this <img> will not necessarily fire it again for a new src,
      // which would strand the chain on a broken image.
      key={current}
      src={current}
      alt={alt}
      width={tiled ? undefined : size}
      height={tiled ? undefined : size}
      className={tiled ? 'platform-icon-tile__img' : undefined}
      onError={() => setAttempt((n) => n + 1)}
    />
  );
}
